import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import type { TrustedContactRelationship, User } from '@interfaces/user';
import { RelatedEntityTypes } from '@interfaces/user';
import { SubStepTypes } from '@interfaces/workflow';
import { getWorkflowCompletionHeaders } from '@ovComponents/4-module/workflowCompletion/workflowCompletionHeaders';
import {
  UserContext,
  useFeatureFlag,
  usePermission,
} from '@providers/userContextProvider';
import { formatPhoneNumber } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { useSnackbar } from 'hephaestus/web';
import { isEmpty, omit } from 'lodash-es';
import { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnabledCustomFields } from '../utils';
import { RelationshipInformationVisual } from './relationshipInformation.visual';

const RELATION_ADDRESS_FIELDS =
  'city streetName postal unitNumber houseNumber neighborhood country jurisdiction';

/**
 * Maps each enabled spouse / trusted-contact option to the field(s) that must be
 * requested on a household relationship user so the value can be auto-filled when
 * that person is selected from the dropdown.
 */
const RELATION_USER_FIELD_SELECTIONS: Record<string, string[]> = {
  spouseFirstName: ['firstName'],
  spouseMiddleName: ['middleName'],
  spouseLastName: ['lastName'],
  spouseEmail: ['primaryEmail'],
  // A user's SIN is stored as `taxId` on the backend, so pull it (and its type)
  // to auto-fill the spouse SIN field when a household member is selected.
  spouseTaxId: ['taxId', 'taxIdType'],
  spouseDateOfBirth: ['dateOfBirth'],
  spouseEmploymentStatus: ['employmentStatus'],
  spouseEmployerName: ['employerName'],
  spouseCompanyType: ['companyType'],
  spouseJobTitle: ['jobTitle'],
  spouseAreaOfStudy: ['studentAreaOfStudy'],
  spouseAnnualIncome: ['annualIncomeCents', 'annualIncomeStr'],
  spouseEmployerCompanyAddress: [
    `employerCompanyAddress { ${RELATION_ADDRESS_FIELDS} }`,
  ],
  trustedContactName: ['firstName', 'lastName'],
  trustedContactEmail: ['primaryEmail'],
  trustedContactPhone: ['phone'],
  trustedContactAddress: [`physicalAddress { ${RELATION_ADDRESS_FIELDS} }`],
};

/**
 * Builds the GraphQL selection for a household relationship user, always including
 * the fields needed to render the dropdown (id, email, first name) plus the fields
 * for every enabled spouse / trusted-contact option (deduped).
 */
const buildRelationUserFields = (
  options: any,
  canReadClientTaxidDisclosed = false,
): string => {
  const fragments = new Set<string>([
    'id: stringId',
    'primaryEmail',
    'firstName',
    'lastName',
  ]);
  Object.entries(RELATION_USER_FIELD_SELECTIONS).forEach(
    ([optionKey, frags]) => {
      if (options?.[optionKey]?.enabled) {
        frags.forEach((fragment) => fragments.add(fragment));
      }
    },
  );
  // The disclosed (unmasked) SIN requires an explicit permission.
  if (options?.spouseTaxId?.enabled && canReadClientTaxidDisclosed) {
    fragments.add('taxIdDisclosed');
  }
  return Array.from(fragments).join('\n              ');
};

const FETCH_USER = (
  permissions: { canReadClientTaxidDisclosed?: boolean },
  derivedCustomFields?: string[],
  relationUserFields = 'id: stringId primaryEmail firstName',
  includeHouseholds = false,
) => gql`
  query fetchUserRelationshipInfo($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        ${
          includeHouseholds
            ? `households {
          id: stringId
          relationships {
            user {
              ${relationUserFields}
            }
          }
        }
        relatedEntities {
          relation
          entity {
            id: stringId
          }
        }`
            : ''
        }
        maritalStatus
        spouseFirstName
        spouseMiddleName
        spouseLastName
        spouseEmail
        spouseTaxIdType
        spouseTaxId
        ${permissions.canReadClientTaxidDisclosed ? 'spouseTaxIdDisclosed' : ''}
        spouseDateOfBirth
        spouseEmploymentStatus
        spouseEmployerName
        spouseEmployerCompanyAddress { city streetName postal unitNumber houseNumber neighborhood country jurisdiction }
        spouseEmployerCompanyPhone
        spouseEmploymentType
        spouseCompanyType
        spouseJobTitle
        spouseAreaOfStudy
        spouseAnnualIncomeCents
        spouseAnnualIncomeStr
        numberOfDependents
        trustedContactPerson {
          name
          relation
          physicalAddress { city streetName postal unitNumber houseNumber neighborhood country jurisdiction }
          email
          phone
        }
        type
        countryOfTaxResidence
        physicalAddress { country }
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

const UPDATE_USER_RELATED_ENTITIES = gql`
  mutation updateUserRelatedEntities(
    $userId: ID!
    $relatedEntities: [RelatedEntityInput]
  ) {
    updateUser(input: { userId: $userId, relatedEntities: $relatedEntities }) {
      user {
        id
      }
    }
  }
`;

const INITIAL_USER_DATA: Partial<User> = {
  maritalStatus: undefined,
  spouseFirstName: '',
  spouseMiddleName: '',
  spouseLastName: '',
  spouseEmail: '',
  spouseTaxId: '',
  spouseTaxIdType: undefined,
  spouseEmploymentType: '',
  spouseCompanyType: '',
  spouseJobTitle: '',
  spouseAreaOfStudy: '',
  spouseDateOfBirth: '',
  spouseEmploymentStatus: undefined,
  spouseEmployerName: '',
  spouseEmployerCompanyAddress: undefined,
  spouseEmployerCompanyPhone: '',
  spouseAnnualIncomeCents: undefined,
  spouseAnnualIncomeStr: undefined,
  numberOfDependents: 0,
  customFields: [],
  trustedContactPerson: {
    name: '',
    physicalAddress: {
      city: '',
      jurisdiction: '',
      streetName: '',
      postal: '',
      unitNumber: '',
      houseNumber: '',
      neighborhood: '',
      country: undefined,
    },
    email: '',
    phone: '',
    relation: '' as TrustedContactRelationship,
  },
};

// Spouse-scoped keys in the local form state. Used when a relation refetch only
// needs to replace the spouse-derived fields (see mergeFetchedUserIntoState).
const SPOUSE_STATE_KEYS: (keyof User)[] = [
  'spouseFirstName',
  'spouseMiddleName',
  'spouseLastName',
  'spouseEmail',
  'spouseSin',
  'spouseTaxId',
  'spouseTaxIdDisclosed',
  'spouseTaxIdType',
  'spouseEmploymentStatus',
  'spouseEmployerName',
  'spouseEmployerCompanyAddress',
  'spouseEmployerCompanyPhone',
  'spouseCompanyType',
  'spouseJobTitle',
  'spouseAreaOfStudy',
  'spouseDateOfBirth',
  'spouseAnnualIncomeCents',
  'spouseAnnualIncomeStr',
];

// Maps a fetched user into the local form state shape.
const mapFetchedUserToState = (user: any): Partial<User> => ({
  maritalStatus: user.maritalStatus || undefined,
  spouseFirstName: user.spouseFirstName || undefined,
  spouseMiddleName: user.spouseMiddleName || undefined,
  spouseLastName: user.spouseLastName || undefined,
  spouseEmail: user.spouseEmail || undefined,
  spouseSin: user.spouseSin || undefined,
  spouseTaxId: user.spouseTaxId || undefined,
  spouseTaxIdDisclosed: user.spouseTaxIdDisclosed || undefined,
  spouseTaxIdType: user.spouseTaxIdType || undefined,
  spouseEmploymentStatus: user.spouseEmploymentStatus || undefined,
  spouseEmployerName: user.spouseEmployerName || undefined,
  spouseEmployerCompanyAddress: user.spouseEmployerCompanyAddress
    ? {
        city: user.spouseEmployerCompanyAddress?.city || undefined,
        jurisdiction:
          user.spouseEmployerCompanyAddress?.jurisdiction || undefined,
        streetName: user.spouseEmployerCompanyAddress?.streetName || undefined,
        postal: user.spouseEmployerCompanyAddress?.postal || undefined,
        unitNumber: user.spouseEmployerCompanyAddress?.unitNumber || undefined,
        houseNumber:
          user.spouseEmployerCompanyAddress?.houseNumber || undefined,
        neighborhood:
          user.spouseEmployerCompanyAddress?.neighborhood || undefined,
        country: user.spouseEmployerCompanyAddress?.country || undefined,
      }
    : ({} as PhysicalAddress),
  spouseEmployerCompanyPhone: user.spouseEmployerCompanyPhone || undefined,
  spouseCompanyType: user.spouseCompanyType || undefined,
  spouseJobTitle: user.spouseJobTitle || undefined,
  spouseAreaOfStudy: user.spouseAreaOfStudy || undefined,
  spouseDateOfBirth: user.spouseDateOfBirth || null,
  spouseAnnualIncomeCents: user.spouseAnnualIncomeCents ?? undefined,
  spouseAnnualIncomeStr: user.spouseAnnualIncomeStr || undefined,
  numberOfDependents: user.numberOfDependents || 0,
  trustedContactPerson: {
    name: user.trustedContactPerson?.name || undefined,
    physicalAddress: user.trustedContactPerson?.physicalAddress
      ? {
          city: user.trustedContactPerson?.physicalAddress?.city || undefined,
          jurisdiction:
            user.trustedContactPerson?.physicalAddress?.jurisdiction ||
            undefined,
          streetName:
            user.trustedContactPerson?.physicalAddress?.streetName || undefined,
          postal:
            user.trustedContactPerson?.physicalAddress?.postal || undefined,
          unitNumber:
            user.trustedContactPerson?.physicalAddress?.unitNumber || undefined,
          houseNumber:
            user.trustedContactPerson?.physicalAddress?.houseNumber ||
            undefined,
          neighborhood:
            user.trustedContactPerson?.physicalAddress?.neighborhood ||
            undefined,
          country:
            user.trustedContactPerson?.physicalAddress?.country || undefined,
        }
      : ({} as PhysicalAddress),
    email: user.trustedContactPerson?.email || undefined,
    phone: formatPhoneNumber(user.trustedContactPerson?.phone || undefined),
    relation: user.trustedContactPerson?.relation || undefined,
  },
  type: user.type || undefined,
  countryOfTaxResidence: user.countryOfTaxResidence || undefined,
  physicalAddress: user.physicalAddress,
  customFields: user.customFields || [],
});

export const RelationshipInformation = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
  grid = false,
  updateMode = false,
}: {
  options: any;
  userId: string;
  onNext?: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
  grid?: boolean;
  updateMode?: boolean;
}) => {
  const { t } = useTranslation(['shared']);
  const { enqueueSnackbar } = useSnackbar();
  const canReadClientTaxidDisclosed = usePermission(
    'read:client_taxid_disclosed',
  );
  const canReadCustomFields = usePermission('read:custom_fields');
  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const enabledCustomFields = getEnabledCustomFields(options);
  const { activeOrganization } = useContext(UserContext);
  const [userData, setUserData] = useState(INITIAL_USER_DATA);
  // Tracks which relation (if any) triggered the current refetch and whether it
  // was a clear. When set, the query's onCompleted only touches that relation's
  // fields instead of the whole form, so unsaved edits to other fields are not
  // discarded. On a clear we reset those fields rather than merging the fetched
  // payload, since a lagging refetch can still return the removed entity.
  const pendingRelationRefetchRef = useRef<{
    relation: RelatedEntityTypes;
    cleared: boolean;
  } | null>(null);
  const tcpSpouseLookupEnabled = useFeatureFlag(
    FlagsmithFlags.tcp_spouse_lookup,
  );
  const relationUserFields = useMemo(
    () => buildRelationUserFields(options, canReadClientTaxidDisclosed),
    [options, canReadClientTaxidDisclosed],
  );

  const customFieldsPermitted =
    canReadCustomFields && enabledCustomFields.length !== 0;

  // loading 1 - custom fields
  const { loading: customFieldsLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.RELATIONSHIP_INFORMATION,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !customFieldsPermitted,
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map(
        (a: any) => a.key,
      );
      setDerivedCustomFieldsKeys(customFieldKeys);
    },
  });

  // loading 2 - user's data
  const {
    loading,
    data: userQueryData,
    refetch: refetchUser,
  } = useQuery(
    FETCH_USER(
      { canReadClientTaxidDisclosed },
      derivedCustomFieldsKeys,
      relationUserFields,
      tcpSpouseLookupEnabled,
    ),
    {
      variables: {
        userId,
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: customFieldsLoading,
      onCompleted: (data) => {
        const mapped = mapFetchedUserToState(data.fetchUser.user);
        const pending = pendingRelationRefetchRef.current;
        pendingRelationRefetchRef.current = null;
        // Initial load (or any non-relation refetch): populate the whole form.
        if (!pending) {
          setUserData(mapped);
          return;
        }
        const { relation, cleared } = pending;
        setUserData((prev) => {
          if (relation === RelatedEntityTypes.Spouse) {
            // Reset/replace exactly the keys a select could have merged so a
            // clear is symmetric with a select and no field lingers (e.g.
            // spouseEmployerCompanyPhone / spouseSin, which the optimistic
            // applySpouseFromUser({}) never touches). INITIAL spouse values are
            // safe: no relation field and an undefined address that passes the
            // submit isEmpty check.
            const source = cleared ? INITIAL_USER_DATA : mapped;
            const spouseFields = Object.fromEntries(
              SPOUSE_STATE_KEYS.map((key) => [key, (source as any)[key]]),
            );
            return { ...prev, ...spouseFields };
          }
          // TCP clear: keep the optimistic reset, which preserves relation (never
          // auto-filled) and a truly-empty {} address; a lagging refetch could
          // otherwise refill the fields or install an empty-string address shell
          // that survives the submit isEmpty check.
          if (cleared) return prev;
          return { ...prev, trustedContactPerson: mapped.trustedContactPerson };
        });
      },
    },
  );

  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER, {
    context: {
      headers: getWorkflowCompletionHeaders(workflowCompletion),
    },
    onCompleted: () => {
      if (updateMode) {
        enqueueSnackbar(t('profileDetailsUpdated'), { variant: 'success' });
      }
      onNext?.();
    },
    variables: {
      input: {
        ...omit(userData, [
          'type',
          'countryOfTaxResidence',
          'physicalAddress',
          'spouseTaxIdDisclosed',
        ]),
        trustedContactPerson: {
          ...userData.trustedContactPerson,
          physicalAddress: isEmpty(
            userData?.trustedContactPerson?.physicalAddress,
          )
            ? undefined
            : userData?.trustedContactPerson?.physicalAddress,
        },
        spouseEmployerCompanyAddress: isEmpty(
          userData.spouseEmployerCompanyAddress,
        )
          ? undefined
          : userData.spouseEmployerCompanyAddress,
        ...(enabledCustomFields && derivedCustomFields && userData?.customFields
          ? {
              customFields: modifyUpdatingCustomFields(
                userData.customFields,
                derivedCustomFields,
                derivedCustomFieldsKeys,
              ),
            }
          : {}),
        userId,
      },
    },
    refetchQueries: [FETCH_USER({ canReadClientTaxidDisclosed })],
  });

  // Persist the selected household member as a related entity (SPOUSE /
  // TRUSTED_CONTACT). The backend resolves the spouse / trusted-contact fields
  // from relatedEntities, so we refetch the user afterwards to auto-fill them.
  const [updateUserRelatedEntities, { loading: updatingRelatedEntities }] =
    useMutation(UPDATE_USER_RELATED_ENTITIES, {
      context: {
        headers: getWorkflowCompletionHeaders(workflowCompletion),
      },
      onCompleted: () => {
        refetchUser();
      },
    });

  const handleSelectRelationEntity = async (
    entityId: string,
    relation: RelatedEntityTypes,
  ) => {
    if (!entityId) return;
    // Keep other relations intact, but replace any existing entry for this
    // relation (a user can only have one spouse / trusted contact).
    const existingRelatedEntities = (
      userQueryData?.fetchUser?.user?.relatedEntities ?? []
    )
      .filter((re: any) => re?.entity?.id && re.relation !== relation)
      .map((re: any) => ({ entityId: re.entity.id, relation: re.relation }));

    pendingRelationRefetchRef.current = { relation, cleared: false };
    try {
      await updateUserRelatedEntities({
        variables: {
          userId,
          relatedEntities: [...existingRelatedEntities, { entityId, relation }],
        },
      });
    } catch (error) {
      // Persisting the relation failed: clear the pending flag and surface the
      // error so the visual can roll back its optimistic selection/auto-fill.
      pendingRelationRefetchRef.current = null;
      enqueueSnackbar(t('relationshipInformation.relationUpdateError'), {
        variant: 'error',
      });
      throw error;
    }
  };

  // Remove the spouse / trusted-contact related entity when the selection is
  // cleared, then refetch so the resolved fields are unlocked / emptied.
  const handleClearRelationEntity = async (relation: RelatedEntityTypes) => {
    const remainingRelatedEntities = (
      userQueryData?.fetchUser?.user?.relatedEntities ?? []
    )
      .filter((re: any) => re?.entity?.id && re.relation !== relation)
      .map((re: any) => ({ entityId: re.entity.id, relation: re.relation }));

    pendingRelationRefetchRef.current = { relation, cleared: true };
    try {
      await updateUserRelatedEntities({
        variables: {
          userId,
          relatedEntities: remainingRelatedEntities,
        },
      });
    } catch (error) {
      // Removing the relation failed: clear the pending flag and surface the
      // error so the visual can restore its previous selection/values.
      pendingRelationRefetchRef.current = null;
      enqueueSnackbar(t('relationshipInformation.relationUpdateError'), {
        variant: 'error',
      });
      throw error;
    }
  };

  // Household relationship users available to pick as spouse / trusted contact.
  // Deduped by id and excluding the account holder themselves. Behind the
  // tcp_spouse_lookup flag: when disabled there are no options, so the dropdowns
  // never render and the widget behaves exactly as before.
  const relationOptions = useMemo(() => {
    if (!tcpSpouseLookupEnabled) return [];
    const users = (userQueryData?.fetchUser?.user?.households ?? [])
      .flatMap((household: any) => household?.relationships ?? [])
      .map((relationship: any) => relationship?.user)
      .filter(
        (relationUser: any) => relationUser && relationUser.id !== userId,
      );
    return Array.from(
      new Map(
        users.map((relationUser: any) => [relationUser.id, relationUser]),
      ).values(),
    );
  }, [userQueryData, userId, tcpSpouseLookupEnabled]);

  // Pre-select the spouse / trusted-contact dropdowns from any already-saved
  // related entities so the widget reflects the existing selection on load.
  const { selectedSpouseEntityId, selectedTrustedContactEntityId } =
    useMemo(() => {
      if (!tcpSpouseLookupEnabled) {
        return {
          selectedSpouseEntityId: '',
          selectedTrustedContactEntityId: '',
        };
      }
      const relatedEntities =
        userQueryData?.fetchUser?.user?.relatedEntities ?? [];
      return {
        selectedSpouseEntityId:
          relatedEntities.find(
            (re: any) => re?.relation === RelatedEntityTypes.Spouse,
          )?.entity?.id ?? '',
        selectedTrustedContactEntityId:
          relatedEntities.find(
            (re: any) => re?.relation === RelatedEntityTypes.TrustedContact,
          )?.entity?.id ?? '',
      };
    }, [userQueryData, tcpSpouseLookupEnabled]);

  return (
    <RelationshipInformationVisual
      options={options}
      userData={userData}
      updateUser={setUserData}
      updating={updating}
      continueFunc={updateUser}
      loading={
        loading || stepLoading || customFieldsLoading || updatingRelatedEntities
      }
      grid={grid}
      updateMode={updateMode}
      workflowCompletion={workflowCompletion}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
      relationOptions={relationOptions}
      onSelectRelationEntity={handleSelectRelationEntity}
      onClearRelationEntity={handleClearRelationEntity}
      selectedSpouseEntityId={selectedSpouseEntityId}
      selectedTrustedContactEntityId={selectedTrustedContactEntityId}
      resolvedUser={userQueryData?.fetchUser?.user}
    />
  );
};

export default RelationshipInformation;
